import { useState, useEffect, useCallback, useRef } from "react";
import { View, StyleSheet } from "react-native";
import MessagesContainer, { type MessageItem } from "./MessagesContainer";
import ChatEmptyState from "./ChatEmptyState";
import { Todo, AttachmentType } from "../../graphql/generated/graphql";
import useCompanionLlmStream from "../../hooks/useCompanionLlmStream";
import { v4 as uuidv4 } from "uuid";
import { WorkspaceHeader } from "../../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import { ThreadSelector } from "./ThreadSelector";
import { useWorkspaceThreads } from "../../contexts/CompanionThreadsContext";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import ChatInputController from "./ChatInputController";
import { Assignment } from "../../components/chatNewInterface/interfaceComponents/modals/AddAssignmentModal";
import type { AttachmentInputType, InitialChatMessage } from "./types";
import { GENERAL_WORKSPACE_ID } from "../../hooks/useWorkspaceLists";

const NEW_CHAT_KEY = "new-chat";

interface ChatWindowContainerProps {
  chatId: string | null;
  onChatIdChange?: (chatId: string) => void;
  isLoadingChatId?: boolean;
  initialMessage?: InitialChatMessage;
  onInitialMessageConsumed?: () => void;
}

const ChatWindowContainer = ({
  chatId,
  onChatIdChange,
  isLoadingChatId = false,
  initialMessage,
  onInitialMessageConsumed,
}: ChatWindowContainerProps) => {
  const [messagesByChatId, setMessagesByChatId] = useState<Record<string, MessageItem[]>>({});
  const [streamingMessageIds, setStreamingMessageIds] = useState<Record<string, string | null>>({});
  const [streamingChatKey, setStreamingChatKey] = useState<string | null>(null);
  const [resetAutoScroll, setResetAutoScroll] = useState(0);
  const [preselectedAssignment, setPreselectedAssignment] = useState<Assignment | null>(null);
  const {
    activeWorkspaceId,
    setActiveWorkspaceId,
    setOpenWorkspaceId,
    refetchPreviewChats,
    getThreadName: getThreadNameFromContext,
    getWorkspaceName,
    courseWorkspaces,
    activeChatThread,
    activeChatThreadLoading,
    activeChatError,
    refetchActiveChat,
    showError,
  } = useWorkspaceThreads();
  const hasStreamedRef = useRef<Set<string>>(new Set());
  const prevChatIdRef = useRef<string | null>(null);
  const latestChatIdRef = useRef<string | null>(chatId);
  const hasFirstTokenRef = useRef(false);
  const [hasReceivedFirstToken, setHasReceivedFirstToken] = useState(false);

  const { startStream, cancelStream, isStreaming } = useCompanionLlmStream();
  const initialMessageKeyRef = useRef<string | null>(null);
  const initialWorkspaceIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (initialMessage?.workspaceId !== undefined) {
      if (initialMessage.workspaceId !== GENERAL_WORKSPACE_ID) {
        initialWorkspaceIdRef.current = initialMessage.workspaceId;
      } else {
        initialWorkspaceIdRef.current = null;
      }
      setActiveWorkspaceId(initialMessage.workspaceId);
      if (initialMessage.workspaceId !== GENERAL_WORKSPACE_ID) {
        setOpenWorkspaceId(initialMessage.workspaceId);
      } else {
        setOpenWorkspaceId(null);
      }
    }
  }, [initialMessage?.workspaceId, setActiveWorkspaceId, setOpenWorkspaceId]);

  const chatWorkspaces = activeChatThread?.workspacesHasChat ?? [];

  const resolvedWorkspaceId =
    activeWorkspaceId && chatWorkspaces.some((w) => w.id === activeWorkspaceId)
      ? activeWorkspaceId
      : (chatWorkspaces[0]?.id ?? activeWorkspaceId ?? GENERAL_WORKSPACE_ID);

  const getThreadName = useCallback(
    (threadId: string | null): string => {
      if (!threadId) return "";

      const contextName = getThreadNameFromContext(threadId);
      if (contextName) {
        return contextName;
      }

      return activeChatThread?.id === threadId ? (activeChatThread?.name ?? "") : "";
    },
    [activeChatThread, getThreadNameFromContext]
  );

  const currentWorkspaceType: "general" | "course" = (() => {
    if (resolvedWorkspaceId === GENERAL_WORKSPACE_ID) {
      return "general";
    }

    // First try to get type from chat data (when chatId exists)
    const workspaceFromChat = chatWorkspaces.find((w) => w.id === resolvedWorkspaceId);
    if (workspaceFromChat?.type) {
      return workspaceFromChat.type === "course" ? "course" : "general";
    }

    // Fallback to workspace lists from context (for new chats during streaming)
    if (courseWorkspaces.some((w) => w.id === resolvedWorkspaceId)) {
      return "course";
    }

    return "general";
  })();

  const workspaceName = getWorkspaceName(resolvedWorkspaceId) ?? "General Chat";

  const getChatKey = useCallback((id: string | null) => id ?? NEW_CHAT_KEY, []);
  const currentChatKey = getChatKey(chatId);
  const messages = messagesByChatId[currentChatKey] ?? [];
  const currentStreamingMessageId = streamingMessageIds[currentChatKey] ?? null;
  const isStreamingForCurrentChat = isStreaming && streamingChatKey === currentChatKey;

  useEffect(() => {
    latestChatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    const prevChatId = prevChatIdRef.current;
    prevChatIdRef.current = chatId;

    //  New chat button: chatA → null
    if (prevChatId && !chatId) {
      setPreselectedAssignment(null);
      setResetAutoScroll((prev) => prev + 1);
      setMessagesByChatId((prev) => ({ ...prev, [NEW_CHAT_KEY]: [] }));
      setStreamingMessageIds((prev) => ({ ...prev, [NEW_CHAT_KEY]: null }));
      initialWorkspaceIdRef.current = null;
      return;
    }

    // Switching chats: chatA → chatB
    if (prevChatId && chatId && prevChatId !== chatId) {
      setResetAutoScroll((prev) => prev + 1);
      return;
    }

    //  First stream completion: null → chatId
    // DO NOTHING
  }, [chatId]);

  useEffect(() => {
    const conversation = activeChatThread?.conversation;
    const dataChatId = activeChatThread?.id;

    if (!chatId) return;
    if (!conversation) return;
    if (dataChatId !== chatId) return;

    // HARD STOP: stream owns messages
    if (hasStreamedRef.current.has(chatId)) {
      return;
    }

    const conversationMessages: MessageItem[] = conversation
      .map((msg) => ({
        id: msg.id,
        sender: msg.sender || "ai",
        content: msg.content || "",
        created: msg.created || undefined,
        todos: msg.todos as Todo[],
        assignments: msg.assignments as Assignment[],
        files: "files" in msg ? (msg as { files?: Array<{ id: string; fileName: string }> }).files : undefined,
      }))
      .reverse();

    setMessagesByChatId((prev) => ({
      ...prev,
      [chatId]: conversationMessages,
    }));
  }, [chatId, activeChatThread]);

  const handleChatRenamed = useCallback(async () => {
    try {
      await refetchPreviewChats();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to refresh the thread list.";
      showError({
        title: "Failed to refresh thread list",
        message,
        onRetry: async () => {
          await refetchPreviewChats();
        },
        retryButtonText: "Retry",
      });
    }
  }, [refetchPreviewChats, showError]);

  const handleSubmit = useCallback(
    async (
      message: string,
      attachment: AttachmentInputType | null,
      options?: { workspaceIdOverride?: string | null }
    ) => {
      if (!message.trim() || isStreaming) {
        return;
      }

      const trimmedInput = message.trim();

      const attachedTodos =
        attachment?.type === "todo" ? [{ id: attachment.id, name: attachment.name, date: attachment.date }] : [];
      const attachedAssigment =
        attachment?.type === "assignment"
          ? [{ id: attachment.id, name: attachment.name, due_date: attachment.date }]
          : [];
      const attachedFiles =
        attachment?.type === AttachmentType.Document && attachment.fileId && attachment.name
          ? [{ id: attachment.fileId, fileName: attachment.name }]
          : [];

      const userMessage: MessageItem = {
        id: uuidv4(),
        sender: "human",
        content: trimmedInput,
        todos: attachedTodos as Todo[],
        assignments: attachedAssigment as Assignment[],
        files: attachedFiles,
      };

      const aiTempId = uuidv4();
      const streamChatKey = getChatKey(chatId);

      setMessagesByChatId((prev) => {
        const existing = prev[streamChatKey] ?? [];
        return {
          ...prev,
          [streamChatKey]: [...existing, userMessage, { id: aiTempId, sender: "ai", content: "" }],
        };
      });

      setStreamingMessageIds((prev) => ({ ...prev, [streamChatKey]: aiTempId }));
      setStreamingChatKey(streamChatKey);
      setResetAutoScroll((prev) => prev + 1);
      setPreselectedAssignment(null);
      setHasReceivedFirstToken(false);
      hasFirstTokenRef.current = false;

      const cleanupStream = () => {
        setMessagesByChatId((prev) => ({
          ...prev,
          [streamChatKey]: (prev[streamChatKey] ?? []).filter((m) => m.id !== aiTempId),
        }));
        setStreamingMessageIds((prev) => ({ ...prev, [streamChatKey]: null }));
        setStreamingChatKey(null);
      };

      try {
        const resolveWorkspaceId = () => {
          if (options && Object.prototype.hasOwnProperty.call(options, "workspaceIdOverride")) {
            const override = options.workspaceIdOverride;
            if (!override || override === GENERAL_WORKSPACE_ID) {
              return undefined;
            }
            return override;
          }
          if (activeWorkspaceId && activeWorkspaceId !== GENERAL_WORKSPACE_ID) {
            return activeWorkspaceId;
          }
          return undefined;
        };
        const workspaceId = resolveWorkspaceId();

        if (chatId) {
          hasStreamedRef.current.add(chatId);
        }

        await startStream(trimmedInput, {
          chatId: chatId || undefined,
          workspaceId,
          attachment:
            attachment != null
              ? {
                  fileId: attachment.fileId,
                  id: attachment.id,
                  type: attachment.type,
                }
              : undefined,
          onChunk: ({ text }) => {
            if (text && !hasReceivedFirstToken) {
              hasFirstTokenRef.current = true;
              setHasReceivedFirstToken(true);
            }
            setMessagesByChatId((prev) => ({
              ...prev,
              [streamChatKey]: (prev[streamChatKey] ?? []).map((m) =>
                m.id === aiTempId ? { ...m, content: m.content + text } : m
              ),
            }));
          },
          onComplete: async ({ event }) => {
            if (event?.messageId) {
              setMessagesByChatId((prev) => ({
                ...prev,
                [streamChatKey]: (prev[streamChatKey] ?? []).map((m) =>
                  m.id === aiTempId ? { ...m, id: event.messageId } : m
                ),
              }));
            }
            if (event?.chatId) {
              hasStreamedRef.current.add(event.chatId);
            }
            if (event?.chatId && streamChatKey === NEW_CHAT_KEY) {
              setMessagesByChatId((prev) => {
                const next = { ...prev };
                const newChatMessages = next[NEW_CHAT_KEY] ?? [];
                delete next[NEW_CHAT_KEY];
                next[event.chatId] = newChatMessages;
                return next;
              });
            }
            setStreamingMessageIds((prev) => {
              const next = { ...prev };
              if (event?.chatId && streamChatKey === NEW_CHAT_KEY) {
                const newChatStreamingId = next[NEW_CHAT_KEY] ?? null;
                delete next[NEW_CHAT_KEY];
                next[event.chatId] = newChatStreamingId;
                return next;
              }
              next[streamChatKey] = null;
              return next;
            });
            setStreamingChatKey(null);
            if (event?.chatId && streamChatKey === NEW_CHAT_KEY) {
              // Preserve the workspace that was set from initialMessage
              // Only preserve if it's a course workspace (not general)
              if (initialWorkspaceIdRef.current && initialWorkspaceIdRef.current !== GENERAL_WORKSPACE_ID) {
                setActiveWorkspaceId(initialWorkspaceIdRef.current);
              } else if (initialWorkspaceIdRef.current === null) {
                // If initialMessage explicitly set it to general, ensure it stays general
                setActiveWorkspaceId(GENERAL_WORKSPACE_ID);
              }
              if (!latestChatIdRef.current && onChatIdChange) {
                onChatIdChange(event.chatId);
              }
              // Refetch the sidebar preview query to update thread counts
              try {
                await refetchPreviewChats();
              } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to refresh the thread list.";
                showError({
                  title: "Failed to refresh thread list",
                  message,
                  onRetry: async () => {
                    await refetchPreviewChats();
                  },
                  retryButtonText: "Retry",
                });
              }
            }
          },
          onError: ({ error }) => {
            console.error("[ChatWindowContainer] Stream error", { message: error.message });
            cleanupStream();
            showError({
              title: "Failed to send message",
              message: error.message || "An error occurred while sending your message. Please try again.",
              onRetry: async () => {
                await handleSubmit(trimmedInput, attachment ?? null, options);
              },
              retryButtonText: "Retry",
            });
          },
          onChatRenamed: handleChatRenamed,
        });
      } catch (error) {
        console.error("[ChatWindowContainer] Failed to start stream:", error);
        cleanupStream();
        const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
        showError({
          title: "Failed to send message",
          message: errorMessage || "Unable to start the conversation. Please try again.",
          onRetry: async () => {
            await handleSubmit(trimmedInput, attachment ?? null, options);
          },
          retryButtonText: "Retry",
        });
      }
    },
    [
      isStreaming,
      chatId,
      activeWorkspaceId,
      startStream,
      getChatKey,
      hasReceivedFirstToken,
      onChatIdChange,
      handleChatRenamed,
      refetchPreviewChats,
      showError,
    ]
  );

  const handleAssignmentPress = useCallback(
    (assignment: {
      id: string;
      title?: string | null;
      due_date?: string | null;
      courseName?: string;
      courseId?: string;
    }) => {
      const assignmentData: Assignment = {
        id: assignment.id,
        title: assignment.title,
        due_date: assignment.due_date,
        courseName: assignment.courseName,
        courseId: assignment.courseId,
      };
      setPreselectedAssignment(assignmentData);
    },
    []
  );

  const isEmptyState = messages.length === 0 && !isStreamingForCurrentChat && !currentStreamingMessageId;
  const isChatLoading = Boolean(chatId) && (activeChatThreadLoading || isLoadingChatId);
  const shouldShowEmptyState = isEmptyState && !isChatLoading;

  useEffect(() => {
    return () => {
      cancelStream();
    };
  }, [cancelStream]);

  useEffect(() => {
    if (!initialMessage?.text?.trim()) {
      initialMessageKeyRef.current = null;
      return;
    }
    if (isStreaming) {
      return;
    }

    const attachmentKey = initialMessage.attachment
      ? `${initialMessage.attachment.type || ""}:${initialMessage.attachment.id || ""}:${
          initialMessage.attachment.fileId || ""
        }:${initialMessage.attachment.name || ""}:${initialMessage.attachment.date || ""}`
      : "no-attachment";
    const messageKey = `${initialMessage.text.trim()}::${attachmentKey}`;

    if (initialMessageKeyRef.current === messageKey) {
      return;
    }

    initialMessageKeyRef.current = messageKey;
    const workspaceOverride = initialMessage.workspaceId ?? null;
    // Store the initial workspace ID to prevent it from being overridden
    if (workspaceOverride && workspaceOverride !== GENERAL_WORKSPACE_ID) {
      initialWorkspaceIdRef.current = workspaceOverride;
    } else {
      // Explicitly clear if it's general or null
      initialWorkspaceIdRef.current = null;
    }
    setActiveWorkspaceId(workspaceOverride ?? GENERAL_WORKSPACE_ID);
    void handleSubmit(initialMessage.text, initialMessage.attachment ?? null, {
      workspaceIdOverride: workspaceOverride,
    });
    onInitialMessageConsumed?.();
  }, [initialMessage, isStreaming, handleSubmit, onInitialMessageConsumed, setActiveWorkspaceId]);

  const showActiveChatError = Boolean(chatId && activeChatError);

  if (showActiveChatError) {
    return (
      <View style={styles.container}>
        <WorkspaceHeader title={workspaceName} />
        <View style={styles.errorContainer}>
          <CustomText
            text={activeChatError ?? "We were not able to load the chat thread."}
            size="m"
            useTranslationText={false}
            style={styles.errorMessage}
          />
          <CustomButton
            text="Retry"
            onPress={() => void refetchActiveChat()}
            useTranslationText={false}
            style={styles.retryButton}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <WorkspaceHeader
        title={workspaceName}
        middleAction={
          !!chatId && (
            <View style={styles.threadSelector}>
              <ThreadSelector workspaceId={resolvedWorkspaceId} threadId={chatId} threadName={getThreadName(chatId)} />
            </View>
          )
        }
      />
      <View style={styles.content}>
        <View style={styles.messagesArea}>
          <MessagesContainer
            messages={shouldShowEmptyState ? [] : messages}
            isStreaming={isStreamingForCurrentChat}
            currentStreamingMessageId={currentStreamingMessageId}
            resetAutoScroll={resetAutoScroll}
          />
          <View style={styles.chatInputWrapper}>
            <ChatInputController
              onSubmit={handleSubmit}
              disabled={isStreaming}
              placeholder="Type a message..."
              activeWorkspaceType={currentWorkspaceType}
              isEmpty={messages.length === 0}
              showError={showError}
            />
            <View style={styles.disclaimerContainer}>
              <CustomText
                useTranslationText={false}
                text={"Axio can make mistakes. Please verify important information."}
                style={styles.disclaimerText}
              />
            </View>
          </View>
        </View>
        {shouldShowEmptyState && (
          <View style={styles.emptyOverlay} pointerEvents="auto">
            <ChatEmptyState
              title={workspaceName}
              input={
                <ChatInputController
                  onSubmit={handleSubmit}
                  disabled={isStreaming}
                  activeWorkspaceType={currentWorkspaceType}
                  showSuggestions={false}
                  preselectedAssignment={preselectedAssignment}
                  showError={showError}
                />
              }
              onAssignmentPress={handleAssignmentPress}
            />
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
  },
  chatInputWrapper: {
    width: "100%",
    maxWidth: 1152,
    alignSelf: "center",
    zIndex: 900,
    padding: 16,
  },
  content: {
    flex: 1,
    position: "relative",
  },
  emptyOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    elevation: 9999,
    backgroundColor: "#f9fafb",
  },
  messagesArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#f9fafbb3",
  },
  threadSelector: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: [{ translateX: "-50%" }, { translateY: "-50%" }],
  },
  disclaimerContainer: {
    marginTop: 8,
    alignItems: "center",
  },
  disclaimerText: {
    fontSize: 12,
    color: "#667085",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  errorMessage: {
    textAlign: "center",
    marginBottom: 16,
  },
  retryButton: {
    minWidth: 120,
  },
});

export default ChatWindowContainer;
